import { useReferenceStore } from "@app/store/useReferenceStore";

/**
 * Имена типов подразделений (для подсказок и типизации).
 * Реальные коды приходят из API и попадают в {@link useReferenceStore} при bootstrap.
 */
export type DivisionTypeName =
    | "Бригада"
    | "Бюро"
    | "Группа"
    | "Группа подразделений учета"
    | "Дирекция"
    | "Завод"
    | "Кладовая"
    | "Комплекс"
    | "Лаборатория"
    | "Место хранения"
    | "Неопределенный"
    | "Отдел"
    | "Площадка"
    | "Пр-во в пр-ве"
    | "Производственная площадка"
    | "Производственное объединение"
    | "Производство"
    | "Прочее"
    | "Рабочий центр"
    | "Склад"
    | "Служба"
    | "Управление"
    | "Участок"
    | "Центр"
    | "Цех";

function getDivisionTypeMap(): Record<string, number> {
    const types = useReferenceStore.getState().divisionTypes;
    return Object.fromEntries(types.map((t) => [t.name, t.code]));
}

/**
 * Коды типов подразделений по имени. Данные берутся из {@link useReferenceStore} в момент обращения.
 * Хук стора в компонентах не нужен: используйте `DivisionType.Склад`, `DivisionType.Цех` и т.д.
 * Если справочник ещё не загружен при bootstrap, обращение по ключу выбросит ошибку.
 *
 * @example
 * ```ts
 * flt={{ Types: [DivisionType.Склад, DivisionType.Цех] }}
 * ```
 */
export const DivisionType: { [K in DivisionTypeName]: number } = new Proxy({} as { [K in DivisionTypeName]: number }, {
    get(_, prop: string) {
        const map = getDivisionTypeMap();
        const value = map[prop];
        if (value === undefined) {
            throw new Error(
                `DivisionType["${prop}"]: справочник типов подразделений не загружен или тип "${prop}" отсутствует. Проверьте bootstrap и наличие данных в useReferenceStore.`,
            );
        }
        return value;
    },
});

/**
 * Наименование типа подразделения по коду (данные из {@link useReferenceStore}).
 */
export function getDivisionTypeName(code: number): string {
    const item = useReferenceStore.getState().divisionTypes.find((t) => t.code === code);
    return item?.name ?? "";
}
